<script lang="ts" setup>
import userStore from '@/store/UserStore'
import { StepsController } from '@/controllers/StepsController'
import projectStore from '@/store/ProjectStore'
import formStore from '@/store/FormStore'
import { computed, ref, watch } from 'vue'
import { CharacteristicType, FuelType, ProductType, RecommendationBadgeType } from '@/helper/Types'
import { BackupHeaterCharacteristic } from '@/services/models/BackupHeaterCharacteristic'
import i18n, { formatNumber } from '@/helper/Translations'
import { BackupHeater } from '@/services/models/implementations/BackupHeater'
import ProductsSelectTable, { ProductColumn } from '@/components/ProductsSelectTable.vue'
import { ValidatorType } from '@/helper/ValidateDirective'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ExistingBackupHeater from '@/components/steps/components/ExistingBackupHeater.vue'
import BackupHeaterFilterBar from '@/components/steps/components/BackupHeaterFilterBar.vue'
import ExclusionHint from '@/components/steps/components/ExclusionHint.vue'
import { BackupHeaterFilter, BoilerType } from '@/models/BackupHeaterFilter'
import ProductAvailabilityLink from '@/components/ProductAvailabilityLink.vue'
import { ProductResponse } from '@/services/models/ProductResponse.ts'
import ProjectController from '@/controllers/ProjectController.ts'

defineProps<{
  validator: ValidatorType
}>()

const emit = defineEmits<{
  (e: 'change-existing-backup-heater-checkbox', value: boolean): void
}>()

const backupHeaters = computed(() => projectStore.backupHeaters)
const filteredBackupHeaters = computed(() => {
  return backupHeaters.value.filter(buh => {

    if (filter.value.boilerType) {
      const boilerType = buh.chr.dhwCapable ? BoilerType.COMBI_BOILER : BoilerType.SYSTEM_BOILER

      if (filter.value.boilerType !== boilerType) {
        return false
      }
    }

    if (filter.value.mounting && buh.chr.mounting !== filter.value.mounting) {
      return false
    }

    return !(filter.value.fuelType && buh.chr.fuelType !== filter.value.fuelType)
  })
})

const integratedBackupHeater = computed<BackupHeater>(() => {
  const additionalProduct = projectStore.getSelectedProduct(ProductType.ADDITIONAL_PRODUCT)
  const heatPump = projectStore.getSelectedProduct(ProductType.HEATPUMP)

  if (projectStore.getProductCharacteristic(additionalProduct, CharacteristicType.BACKUP_HEATER)) {
    return new BackupHeater(additionalProduct)
  } else (projectStore.getProductCharacteristic(heatPump, CharacteristicType.BACKUP_HEATER))
  {
    return new BackupHeater(heatPump)
  }
})

const hasIntegratedBackupHeater = computed((): boolean => {
  const additionalProduct = projectStore.getSelectedProduct(ProductType.ADDITIONAL_PRODUCT)
  const heatPump = projectStore.getSelectedProduct(ProductType.HEATPUMP)

  const backupHeater = (projectStore.getProductCharacteristic(additionalProduct, CharacteristicType.BACKUP_HEATER) ||
    projectStore.getProductCharacteristic(heatPump, CharacteristicType.BACKUP_HEATER)) as BackupHeaterCharacteristic

  const fuelType = backupHeater?.fuelType

  if (fuelType === undefined) {
    return false
  }

  if (projectStore.projectProperties.hybrid.val) {
    return fuelType !== FuelType.ELECTRICITY
  } else {
    return fuelType === FuelType.ELECTRICITY
  }
})

const filter = ref(new BackupHeaterFilter())
const useExistingBackupHeater = ref<boolean>(!!projectStore.projectProperties.existingBackupHeater.val?.name)
const isHeatLossCovered = (projectStore.selectedHeatpump?.chr?.heating?.loadCoveragePercent || 0) >= 100
const backupHeaterSalePackage = ref<boolean>(false)
const isBackupHeaterOptional = (userStore.backupHeaterOptional || isHeatLossCovered || hasIntegratedBackupHeater.value) && !projectStore.projectProperties.hybrid.val
const includeBackupHeaterChecked = ref<boolean>(((!isHeatLossCovered && !hasIntegratedBackupHeater.value) || formStore.data.backupHeaterId !== '') && !projectStore.projectProperties.explicitlyNoBackupHeaterSelected.val)

formStore.data.explicitlyNoBackupHeaterSelected = !includeBackupHeaterChecked.value

watch(() => backupHeaters.value, value => {
  const selectedStorage = value.find(backupHeater => backupHeater.inSalePackage === true)
  if (selectedStorage) {
    includeBackupHeaterChecked.value = true
    backupHeaterSalePackage.value = true

    formStore.data.backupHeaterId = selectedStorage.id
  } else {
    backupHeaterSalePackage.value = false
  }

}, { immediate: true })

const backupHeaterColumns: ProductColumn[] = [
  {
    cssClass: 'is--product',
    columnName: 'step_components.table_product',
    valCb: (val: ProductResponse) => val.name
  },
  {
    cssClass: 'is--power',
    columnName: 'step_components.table_power',
    valCb: (val: ProductResponse) => `${formatNumber((val as BackupHeater).chr?.powerOutput)} kW`
  },
  {
    cssClass: 'is--flow-rate',
    columnName: 'step_components.table_flow_rate',
    valCb: (val: ProductResponse) => {
      const buh = (val as BackupHeater).chr

      if (buh?.tappingClass) {
        return buh.tappingClass
      }

      if (buh?.dhwFlowRate) {
        return `${formatNumber(buh.dhwFlowRate)} l/min`
      }
    }
  },
  {
    cssClass: 'is--fuel',
    columnName: 'step_components.table_fuel_type',
    valCb: (val: ProductResponse) => i18n.global.t(`fuel_types.${(val as BackupHeater).chr?.fuelType}`).toString()
  }
]

function backupHeaterIncludedClicked(): void {
  formStore.data.backupHeaterId = ''
  formStore.data.explicitlyNoBackupHeaterSelected = includeBackupHeaterChecked.value
  StepsController.currentStepValid(false)
}

function changeExistingBackupHeaterCheckbox(value: boolean) {
  formStore.data.backupHeaterId = ''
  formStore.data.existingBackupHeater = {}

  emit('change-existing-backup-heater-checkbox', value)
  StepsController.currentStepValid(false)
}

function validate(): boolean {
  if (formStore.data.explicitlyNoBackupHeaterSelected) {
    return true
  }

  if (projectStore.projectProperties.hybrid.val) {
    return !!formStore.data.backupHeaterId || !!formStore.data.existingBackupHeater?.name
  }
  return !!formStore.data.backupHeaterId || isHeatLossCovered || hasIntegratedBackupHeater.value
}

watch(
  [
    () => formStore.data.backupHeaterId,
    () => formStore.data.powerOutputIntegratedBackupHeater
  ],
  () => {
    if (!hasIntegratedBackupHeater.value) {
      return
    }

    const { backupHeaterId, powerOutputIntegratedBackupHeater } = formStore.data
    const selectedBackupHeater = backupHeaters.value.find((buh) => buh.id === formStore.data.backupHeaterId)
    const powerOutput = backupHeaterId && selectedBackupHeater
      ? Math.max(powerOutputIntegratedBackupHeater, selectedBackupHeater.chr.powerOutput)
      : powerOutputIntegratedBackupHeater

    if (powerOutput) {
      ProjectController.loadProducts(
        ProductType.BUFFERTANK,
        projectStore.projectId,
        `backupHeaterPowerOutputString=${powerOutput}`
      )
    }
  },
  { immediate: true }
)

defineExpose({ validate })
</script>

<template>
  <form id="backup-heater-form" class="steps__form steps-table" @submit.prevent="">
    <div class="steps-table__header">
      <SectionHeadline class="steps-table__title fTable__title" translation-key="step_components.title_backup_heater" />

      <!-- Include backup heater checkbox -->
      <label v-if="isBackupHeaterOptional" class="form__checkbox steps-table__toggle">
        {{ $t('step_components.include_backup_heater') }}
        <input
          id="cb-backup-heater-included"
          v-model="includeBackupHeaterChecked"
          type="checkbox"
          :disabled="backupHeaterSalePackage"
          @click="backupHeaterIncludedClicked"
        >
        <span class="form__cm is--large" />
      </label>

      <!-- Existing backup heater checkbox -->
      <label v-if="projectStore.projectProperties.hybrid.val" class="form__checkbox steps-table__toggle">
        {{ $t('step_components.use_existing_backup_heater') }}
        <input
          id="cb-use-existing-backup-heater" v-model="useExistingBackupHeater"
          type="checkbox"
          @update:model-value="changeExistingBackupHeaterCheckbox"
        >
        <span class="form__cm is--large" />
      </label>

      <ProductAvailabilityLink :availability-link="userStore.productAvailabilityLinks?.backupHeaters" />
    </div>

    <ExistingBackupHeater
      v-if="useExistingBackupHeater"
      :validator="validator"
      @input="StepsController.currentStepValid(false)"
    />

    <template v-else>
      <div v-if="!isBackupHeaterOptional || includeBackupHeaterChecked">
        <BackupHeaterFilterBar
          v-if="backupHeaters.length > 1"
          :backup-heaters="backupHeaters"
          @filter-changed="updatedFilter => filter = updatedFilter"
        />
        <ProductsSelectTable
          v-if="filteredBackupHeaters.length > 0"
          v-model="formStore.data.backupHeaterId"
          :columns="backupHeaterColumns"
          :products="filteredBackupHeaters"
          :recommendation-type="RecommendationBadgeType.LEFT"
          :unselectable="false"
          :preselected-product="hasIntegratedBackupHeater ? integratedBackupHeater : undefined"
          :preselected-product-info="hasIntegratedBackupHeater ? $t('step_components.buh_has_integrated') : ''"
          product-name="backup-heater"
        />
        <ExclusionHint
          v-else
          :text="$t('step_components.no_backup_heaters_available_text')"
          :title="$t('step_components.no_backup_heaters_available')"
        />
      </div>

      <template v-else>
        <ProductsSelectTable
          v-if="hasIntegratedBackupHeater"
          :columns="backupHeaterColumns"
          :products="[]"
          :unselectable="false"
          :preselected-product="integratedBackupHeater"
          :preselected-product-info="$t('step_components.buh_has_integrated')"
          product-name="backup-heater"
        />
        <ExclusionHint
          v-else
          :text="$t('step_components.backup_heater_not_necessary_text')"
          :title="$t('step_components.backup_heater_not_necessary')"
        />
      </template>
    </template>
  </form>
</template>
